import {AlignmentViewRow} from 'search/blast/AlignmentView';
import {boundMinMax, getTotalComponentSize} from 'search/blast/BLASTAlignmentUtil';

export interface Point {
  x: number;
  y: number;
}

export interface AlignmentSelect {
  residueStartX: number;
  residueEndX?: number;
  rowHspIds?: Array<string>;
}

export function setStartSelect(
  setAlignmentSelect: React.Dispatch<React.SetStateAction<AlignmentSelect | null>>,
  residueStartX: number
): void {
  setAlignmentSelect({
    residueStartX: Math.round(residueStartX),
  });
}

export function setEndSelect(
  setAlignmentSelect: React.Dispatch<React.SetStateAction<AlignmentSelect | null>>,
  residueEndX: number,
  pixelEndY: number,
  pixelStartY: number,
  rows: Array<AlignmentViewRow>,
  normalRowHeight: number,
  queryRowHeight: number
): void {
  const roundedResidueEndX = Math.round(residueEndX);

  const topY = Math.min(pixelStartY, pixelEndY);
  const bottomY = Math.max(pixelStartY, pixelEndY);

  // query row is the first row
  const rowStart = Math.floor((topY - queryRowHeight) / normalRowHeight) + 1;
  const rowEnd = Math.ceil((bottomY - queryRowHeight) / normalRowHeight) + 1;
  const rowHspIds = rows.slice(rowStart, rowEnd).flatMap((row) => row.rowHspId);

  setAlignmentSelect((prev) => {
    if (!prev) {
      throw new Error('setEndSelect called without a start select');
    } else {
      return {
        residueStartX: prev.residueStartX,
        residueEndX: roundedResidueEndX,
        rowHspIds,
      };
    }
  });
}

export interface DragSelected {
  residueStart: number;
  residueEnd?: number;
  rowSelected?: boolean;
}

/*
 * Adjusts scroll when zoom changes and there is a selection
 */
export function adjustScrollForZoomChange(
  residueStart: number,
  zoom: number,
  windowLength: number,
  queryLength: number,
  residueEnd?: number
): number {
  const totalComponentSize = getTotalComponentSize(zoom, windowLength, queryLength);
  const maxScroll = totalComponentSize - windowLength;

  const centerResidue = residueEnd ? (residueStart + residueEnd) / 2 : residueStart;
  const centerPixel = (centerResidue / queryLength) * totalComponentSize;

  const newScroll = centerPixel - windowLength / 2;
  return boundMinMax(0, newScroll, maxScroll);
}

export function arrangeStartAndEnd(selectedAlignments: AlignmentSelect, rowHspId: string): DragSelected {
  // selectedAlignments.residueEndX can be 0
  if (selectedAlignments.residueEndX == null) {
    return {
      residueStart: selectedAlignments.residueStartX,
    };
  } else {
    return {
      residueStart: Math.min(selectedAlignments.residueStartX, selectedAlignments.residueEndX),
      residueEnd: Math.max(selectedAlignments.residueStartX, selectedAlignments.residueEndX),
      rowSelected: selectedAlignments.rowHspIds?.includes(rowHspId),
    };
  }
}
